import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Layout, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useAuth } from '../services/api';
import api from '../services/api';
import { toast } from 'sonner';
import { 
  Plus, Edit, Archive, Trash2, Search, FileText, BookOpen, Filter, 
  Bold, Italic, List, ListOrdered, Heading1, Heading2, Link as LinkIcon,
  Underline, Strikethrough, Code, Quote, Table, Minus, RotateCcw,
  MoreVertical, FolderPlus, Highlighter, Undo, Redo
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TipTapLink from '@tiptap/extension-link';
import TipTapUnderline from '@tiptap/extension-underline';
import { Table as TipTapTable } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Highlight from '@tiptap/extension-highlight';
import CodeBlock from '@tiptap/extension-code-block';

// TipTap Wiki-style Editor Component
const WikiEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false, // We use the separate extension
      }),
      TipTapLink.configure({
        openOnClick: false,
      }),
      TipTapUnderline,
      Highlight.configure({
        multicolor: true,
      }),
      CodeBlock,
      TipTapTable.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  if (!editor) {
    return <div className="border rounded-md p-4 min-h-[250px] bg-gray-50">Chargement de l'éditeur...</div>;
  }

  const addTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const setLink = () => {
    const url = window.prompt('URL du lien:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="border rounded-md bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b bg-slate-50">
        {/* Undo/Redo */}
        <Button type="button" variant="ghost" size="sm" onClick={() => editor.chain().focus().undo().run()} className="h-8 w-8 p-0" title="Annuler">
          <Undo className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => editor.chain().focus().redo().run()} className="h-8 w-8 p-0" title="Refaire">
          <Redo className="h-4 w-4" />
        </Button>
        
        <div className="w-px h-6 bg-slate-300 mx-1" />
        
        {/* Headings */}
        <Button type="button" variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'ghost'} size="sm" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className="h-8 w-8 p-0" title="Titre 1">
          <Heading1 className="h-4 w-4" />
        </Button>
        <Button type="button" variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'ghost'} size="sm" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className="h-8 w-8 p-0" title="Titre 2">
          <Heading2 className="h-4 w-4" />
        </Button>
        
        <div className="w-px h-6 bg-slate-300 mx-1" />
        
        {/* Text formatting */}
        <Button type="button" variant={editor.isActive('bold') ? 'default' : 'ghost'} size="sm" onClick={() => editor.chain().focus().toggleBold().run()} className="h-8 w-8 p-0" title="Gras">
          <Bold className="h-4 w-4" />
        </Button>
        <Button type="button" variant={editor.isActive('italic') ? 'default' : 'ghost'} size="sm" onClick={() => editor.chain().focus().toggleItalic().run()} className="h-8 w-8 p-0" title="Italique">
          <Italic className="h-4 w-4" />
        </Button>
        <Button type="button" variant={editor.isActive('underline') ? 'default' : 'ghost'} size="sm" onClick={() => editor.chain().focus().toggleUnderline().run()} className="h-8 w-8 p-0" title="Souligné">
          <Underline className="h-4 w-4" />
        </Button>
        <Button type="button" variant={editor.isActive('strike') ? 'default' : 'ghost'} size="sm" onClick={() => editor.chain().focus().toggleStrike().run()} className="h-8 w-8 p-0" title="Barré">
          <Strikethrough className="h-4 w-4" />
        </Button>
        <Button type="button" variant={editor.isActive('highlight') ? 'default' : 'ghost'} size="sm" onClick={() => editor.chain().focus().toggleHighlight().run()} className="h-8 w-8 p-0" title="Surligné">
          <Highlighter className="h-4 w-4" />
        </Button>
        
        <div className="w-px h-6 bg-slate-300 mx-1" />
        
        {/* Lists */}
        <Button type="button" variant={editor.isActive('bulletList') ? 'default' : 'ghost'} size="sm" onClick={() => editor.chain().focus().toggleBulletList().run()} className="h-8 w-8 p-0" title="Liste à puces">
          <List className="h-4 w-4" />
        </Button>
        <Button type="button" variant={editor.isActive('orderedList') ? 'default' : 'ghost'} size="sm" onClick={() => editor.chain().focus().toggleOrderedList().run()} className="h-8 w-8 p-0" title="Liste numérotée">
          <ListOrdered className="h-4 w-4" />
        </Button>
        
        <div className="w-px h-6 bg-slate-300 mx-1" />
        
        {/* Blocks */}
        <Button type="button" variant={editor.isActive('blockquote') ? 'default' : 'ghost'} size="sm" onClick={() => editor.chain().focus().toggleBlockquote().run()} className="h-8 w-8 p-0" title="Citation">
          <Quote className="h-4 w-4" />
        </Button>
        <Button type="button" variant={editor.isActive('codeBlock') ? 'default' : 'ghost'} size="sm" onClick={() => editor.chain().focus().toggleCodeBlock().run()} className="h-8 w-8 p-0" title="Bloc de code">
          <Code className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => editor.chain().focus().setHorizontalRule().run()} className="h-8 w-8 p-0" title="Ligne horizontale">
          <Minus className="h-4 w-4" />
        </Button>
        
        <div className="w-px h-6 bg-slate-300 mx-1" />
        
        {/* Link & Table */}
        <Button type="button" variant={editor.isActive('link') ? 'default' : 'ghost'} size="sm" onClick={setLink} className="h-8 w-8 p-0" title="Lien">
          <LinkIcon className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={addTable} className="h-8 w-8 p-0" title="Insérer un tableau">
          <Table className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Editor Content */}
      <EditorContent 
        editor={editor} 
        className="prose prose-sm max-w-none p-4 min-h-[250px] focus:outline-none [&_.ProseMirror]:outline-none [&_table]:border-collapse [&_table]:w-full [&_td]:border [&_td]:border-slate-300 [&_td]:p-2 [&_th]:border [&_th]:border-slate-300 [&_th]:p-2 [&_th]:bg-slate-100 [&_th]:font-semibold"
      />
    </div>
  );
};

export default function ProceduresPage() {
  const { user } = useAuth();
  const [procedures, setProcedures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Modal states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  
  // Category management
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({ name: '' });
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: ''
  });

  const isAdmin = user?.role === 'admin';
  const canManage = isAdmin || user?.role === 'gestionnaire';

  const fetchProcedures = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/procedures?include_archived=${showArchived}`);
      setProcedures(response.data);
      
      // Extract unique categories from procedures
      const cats = [...new Set(response.data.map(p => p.category).filter(Boolean))];
      setCategories(cats);
    } catch (error) {
      // Error handled by toast
      toast.error('Erreur lors du chargement des procédures');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    fetchProcedures();
  }, [fetchProcedures]);

  const handleCreate = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Le titre et le contenu sont requis');
      return;
    }

    try {
      await api.post('/procedures', formData);
      toast.success('Procédure créée avec succès');
      setIsCreateOpen(false);
      setFormData({ title: '', content: '', category: '' });
      fetchProcedures();
    } catch (error) {
      toast.error('Erreur lors de la création de la procédure');
    }
  };

  const handleUpdate = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Le titre et le contenu sont requis');
      return;
    }

    try {
      await api.put(`/procedures/${selectedProcedure.id}`, formData);
      toast.success('Procédure mise à jour avec succès');
      setIsEditOpen(false);
      setSelectedProcedure(null);
      setFormData({ title: '', content: '', category: '' });
      fetchProcedures();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour de la procédure');
    }
  };

  const handleArchive = async (procedure) => {
    try {
      await api.put(`/procedures/${procedure.id}/archive`);
      toast.success('Procédure archivée');
      fetchProcedures();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleRestore = async (procedure) => {
    try {
      await api.put(`/procedures/${procedure.id}`, { status: 'active' });
      toast.success('Procédure restaurée');
      fetchProcedures();
    } catch (error) {
      toast.error('Erreur lors de la restauration');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/procedures/${selectedProcedure.id}`);
      toast.success('Procédure supprimée');
      setIsDeleteOpen(false);
      setSelectedProcedure(null);
      fetchProcedures();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Category management
  const handleAddCategory = () => {
    if (!categoryFormData.name.trim()) {
      toast.error('Le nom de la catégorie est requis');
      return;
    }
    
    if (categories.includes(categoryFormData.name.trim())) {
      toast.error('Cette catégorie existe déjà');
      return;
    }
    
    setCategories([...categories, categoryFormData.name.trim()]);
    setCategoryFormData({ name: '' });
    toast.success('Catégorie ajoutée');
  };

  const handleDeleteCategory = (catToDelete) => {
    // Update procedures with this category to have no category
    const updatedProcedures = procedures.map(p => 
      p.category === catToDelete ? { ...p, category: '' } : p
    );
    setProcedures(updatedProcedures);
    setCategories(categories.filter(c => c !== catToDelete));
    toast.success('Catégorie supprimée');
  };

  const openEditModal = (procedure) => {
    setSelectedProcedure(procedure);
    setFormData({
      title: procedure.title,
      content: procedure.content,
      category: procedure.category || ''
    });
    setIsEditOpen(true);
  };

  const openViewModal = (procedure) => {
    setSelectedProcedure(procedure);
    setIsViewOpen(true);
  };

  const openCreateModal = () => {
    setFormData({ title: '', content: '', category: '' });
    setIsCreateOpen(true);
  };

  // Filter procedures
  const filteredProcedures = procedures.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader 
        title="Procédures" 
        description="Base de connaissance et procédures internes"
      />
      
      <div className="p-8 space-y-6" data-testid="procedures-page">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-blue-600" />
            <span className="text-lg font-medium text-slate-700">
              {filteredProcedures.length} procédure{filteredProcedures.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Button variant="outline" onClick={() => setIsCategoryModalOpen(true)} data-testid="manage-categories-btn">
                <FolderPlus className="h-4 w-4 mr-2" />
                Gérer les catégories
              </Button>
            )}
            <Button onClick={openCreateModal} data-testid="create-procedure-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle procédure
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher dans les procédures..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="procedure-search"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[200px]" data-testid="category-filter">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les catégories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            {canManage && (
              <Button
                variant={showArchived ? "default" : "outline"}
                onClick={() => setShowArchived(!showArchived)}
                data-testid="show-archived-btn"
              >
                <Filter className="h-4 w-4 mr-2" />
                {showArchived ? 'Masquer archivées' : 'Voir archivées'}
              </Button>
            )}
            </div>
          </CardContent>
        </Card>

        {/* Procedures Grid */}
        {filteredProcedures.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune procédure trouvée</p>
              <Button onClick={openCreateModal} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Créer une procédure
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredProcedures.map((procedure) => (
              <Card 
                key={procedure.id} 
                className={`hover:shadow-lg transition-shadow cursor-pointer ${procedure.status === 'archived' ? 'opacity-60 border-dashed' : ''}`}
                onClick={() => openViewModal(procedure)}
                data-testid={`procedure-card-${procedure.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg line-clamp-2">{procedure.title}</CardTitle>
                    <div className="flex items-center gap-2">
                      {procedure.status === 'archived' && (
                        <Badge variant="secondary">Archivée</Badge>
                      )}
                      {canManage && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                            {procedure.status === 'active' ? (
                              <>
                                <DropdownMenuItem onClick={() => openEditModal(procedure)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Modifier
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleArchive(procedure)}>
                                  <Archive className="h-4 w-4 mr-2" />
                                  Archiver
                                </DropdownMenuItem>
                              </>
                            ) : (
                              <DropdownMenuItem onClick={() => handleRestore(procedure)}>
                                <RotateCcw className="h-4 w-4 mr-2" />
                                Restaurer
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => { setSelectedProcedure(procedure); setIsDeleteOpen(true); }}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                  {procedure.category && (
                    <Badge variant="outline" className="w-fit">{procedure.category}</Badge>
                  )}
                </CardHeader>
                <CardContent>
                  <div 
                    className="text-sm text-gray-600 line-clamp-3 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(procedure.content.substring(0, 200) + '...') }}
                  />
                  <div className="mt-4 flex items-center justify-between text-xs text-gray-400">
                    <span>Par {procedure.created_by_name}</span>
                    <span>{new Date(procedure.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nouvelle procédure</DialogTitle>
              <DialogDescription>
                Créez une nouvelle procédure pour la base de connaissance
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Titre *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Titre de la procédure"
                    data-testid="procedure-title-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Catégorie</Label>
                  <Select value={formData.category || 'none'} onValueChange={(val) => setFormData({ ...formData, category: val === 'none' ? '' : val })}>
                    <SelectTrigger data-testid="procedure-category-select">
                      <SelectValue placeholder="Sélectionner une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune catégorie</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Contenu *</Label>
                <p className="text-xs text-slate-500 mb-2">Utilisez la barre d'outils pour formater le texte, ajouter des tableaux, des listes, etc.</p>
                <WikiEditor
                  content={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreate} data-testid="save-procedure-btn">
                Créer la procédure
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifier la procédure</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Titre *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    data-testid="edit-procedure-title"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Catégorie</Label>
                  <Select value={formData.category || 'none'} onValueChange={(val) => setFormData({ ...formData, category: val === 'none' ? '' : val })}>
                    <SelectTrigger data-testid="edit-procedure-category">
                      <SelectValue placeholder="Sélectionner une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune catégorie</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Contenu *</Label>
                <WikiEditor
                  content={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleUpdate} data-testid="update-procedure-btn">
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">{selectedProcedure?.title}</DialogTitle>
              <DialogDescription>
                {selectedProcedure?.category && (
                  <Badge variant="outline" className="mr-2">{selectedProcedure.category}</Badge>
                )}
                <span className="text-gray-500">
                  Créé par {selectedProcedure?.created_by_name} le {selectedProcedure && new Date(selectedProcedure.created_at).toLocaleDateString('fr-FR')}
                </span>
              </DialogDescription>
            </DialogHeader>
            <div 
              className="prose prose-sm max-w-none mt-4 [&_table]:border-collapse [&_table]:w-full [&_td]:border [&_td]:border-slate-300 [&_td]:p-2 [&_th]:border [&_th]:border-slate-300 [&_th]:p-2 [&_th]:bg-slate-100"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedProcedure?.content || '') }}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsViewOpen(false)}>
                Fermer
              </Button>
              {canManage && selectedProcedure?.status === 'active' && (
                <Button onClick={() => { setIsViewOpen(false); openEditModal(selectedProcedure); }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Modifier
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Category Management Dialog */}
        <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Gérer les catégories</DialogTitle>
              <DialogDescription>
                Ajoutez ou supprimez des catégories pour organiser vos procédures
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ name: e.target.value })}
                  placeholder="Nouvelle catégorie"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                  data-testid="new-category-input"
                />
                <Button onClick={handleAddCategory} data-testid="add-category-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {categories.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Aucune catégorie</p>
                ) : (
                  categories.map(cat => (
                    <div key={cat} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <span className="text-sm">{cat}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCategory(cat)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-800"
                        data-testid={`delete-category-${cat}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCategoryModalOpen(false)}>
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cette procédure ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. La procédure "{selectedProcedure?.title}" sera définitivement supprimée.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
